import { ReactNode, createContext, useContext, useRef, useState } from 'react';

type CatalogSectionScroller = (sectionId: string) => void;

interface CatalogSectionsContextValue {
  activeSectionId: string;
  setVisibleSectionId: (sectionId: string) => void;
  releasePinnedSection: () => void;
  selectSection: CatalogSectionScroller;
  registerScroller: (scroller: CatalogSectionScroller | null) => void;
}

const CatalogSectionsContext = createContext<CatalogSectionsContextValue>({
  activeSectionId: '',
  setVisibleSectionId: () => undefined,
  releasePinnedSection: () => undefined,
  selectSection: () => undefined,
  registerScroller: () => undefined,
});

interface CatalogSectionsProviderProps {
  children: ReactNode;
}

export const CatalogSectionsProvider = ({ children }: CatalogSectionsProviderProps) => {
  const [visibleSectionId, setVisibleSectionId] = useState('');
  const [pinnedSectionId, setPinnedSectionId] = useState<string | null>(null);
  const scroller = useRef<CatalogSectionScroller | null>(null);

  const selectSection = (sectionId: string) => {
    setPinnedSectionId(sectionId);
    scroller.current?.(sectionId);
  };

  const registerScroller = (next: CatalogSectionScroller | null) => {
    scroller.current = next;
  };

  return (
    <CatalogSectionsContext.Provider
      value={{
        activeSectionId: pinnedSectionId ?? visibleSectionId,
        setVisibleSectionId,
        selectSection,
        registerScroller,
        releasePinnedSection: () => setPinnedSectionId(null),
      }}
    >
      {children}
    </CatalogSectionsContext.Provider>
  );
};

export const useCatalogSections = () => useContext(CatalogSectionsContext);
